import { useRoutes } from 'react-router-dom';
import { useEffect } from 'react';
import routes from './routes';
import { ErrorBoundary } from 'react-error-boundary';
import { Button } from '@/components/ui/button';
import { Toaster } from 'sonner';
import { SearchProvider } from '@/features/recent-links/contexts/search.context';
import { LoadingProvider } from '@/components/providers/LoadingProvider';
import useCollectionStore from '@/features/recent-links/stores/collection.store';
import useAuthStore from '@/features/auth/stores/auth.store';

function ErrorFallback({ error, resetErrorBoundary }: { 
  error: Error, 
  resetErrorBoundary: () => void 
}) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
      <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
      <pre className="text-gray-700 mb-4 p-4 bg-gray-100 rounded">
        {error.message}
      </pre>
      <Button 
        onClick={resetErrorBoundary}
        variant="destructive"
      >
        Try again
      </Button>
    </div>
  );
}

export default function App() {
  const element = useRoutes(routes);
  const { fetchCollections } = useCollectionStore();
  const { isAuthenticated } = useAuthStore();

  // Load collections when the app starts and user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCollections();
    }
  }, [fetchCollections, isAuthenticated]);

  return (
    <ErrorBoundary 
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // Reset the state of your app so the error doesn't happen again
        window.location.reload();
      }}
    >
      <LoadingProvider>
        <SearchProvider>
          {element}
          <Toaster />
        </SearchProvider>
      </LoadingProvider>
    </ErrorBoundary>
  );
}